/* global google */
import React, { useEffect, useState } from 'react';
import { extinguisherService } from '../services/api';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { theme } from '../theme';
import './MapDisplay.css';

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 }; // San Francisco
const DEFAULT_ZOOM = 10;
const SINGLE_POINT_ZOOM = 14;
const MARKER_ICON_MAIN_COLOR = theme.userLocationMarker; // Orange for user location
const MARKER_ICON_EXTINGUISHER_COLOR = theme.extinguisherMarker; // Red for extinguishers

// Helper component to control map view
function MapController({
  userLocation,
  nearbyExtinguishers,
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    const uniqueLocationsForBounds = [];
    
    // Add user location if available
    if (userLocation) {
      uniqueLocationsForBounds.push(userLocation);
    }
    
    // Add extinguisher locations
    nearbyExtinguishers?.forEach(ext => {
      if (ext.location) {
        uniqueLocationsForBounds.push(ext.location);
      }
    });

    if (uniqueLocationsForBounds.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      uniqueLocationsForBounds.forEach(loc => {
        bounds.extend(new google.maps.LatLng(loc.lat, loc.lng));
      });

      if (uniqueLocationsForBounds.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(SINGLE_POINT_ZOOM);
      } else {
        map.fitBounds(bounds, 50); // 50px padding
      }
    } else {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  }, [map, userLocation, nearbyExtinguishers]);

  return null;
}

function ZoomControls() {
  const map = useMap();

  const handleZoomIn = () => {
    if (map) {
      const currentZoom = map.getZoom();
      if (currentZoom !== null && currentZoom !== undefined) {
        map.setZoom(currentZoom + 1);
      }
    }
  };

  const handleZoomOut = () => {
    if (map) {
      const currentZoom = map.getZoom();
      if (currentZoom !== null && currentZoom !== undefined) {
        map.setZoom(currentZoom - 1);
      }
    }
  };

  if (!map) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: '16px',
      right: '16px',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      backgroundColor: 'red', // Debug: make container visible
      padding: '4px'
    }}>
      <button 
        onClick={handleZoomIn}
        aria-label="Zoom in"
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#333',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        +
      </button>
      <button 
        onClick={handleZoomOut}
        aria-label="Zoom out"
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#333',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        -
      </button>
    </div>
  );
}

// Transform API data to expected format
function transformExtinguisherData(extinguishers) {
  return extinguishers.map(ext => ({
    id: ext.id,
    location: { lat: ext.lat, lng: ext.lng },
    address: ext.address,
    expirationDates: [ext.expiration],
    distance: ext.distance,
    imageUrl: ext.image_url,
    createdAt: ext.created_at
  }));
}

function MapDisplay() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [selectedExtinguisherId, setSelectedExtinguisherId] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyExtinguishers, setNearbyExtinguishers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Get user location and load nearby extinguishers
  useEffect(() => {
    const getUserLocationAndLoadExtinguishers = async () => {
      try {
        setIsLoading(true);
        
        // Try to get user location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const location = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(location);
              await loadNearbyExtinguishers(location);
            },
            async (error) => {
              console.log('Geolocation error, using default location:', error.message);
              await loadNearbyExtinguishers(DEFAULT_CENTER);
            },
            {
              timeout: 10000,
              enableHighAccuracy: false,
              maximumAge: 300000
            }
          );
        } else {
          console.log('Geolocation not supported, using default location');
          await loadNearbyExtinguishers(DEFAULT_CENTER);
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        setError('Failed to initialize map');
        setIsLoading(false);
      }
    };

    getUserLocationAndLoadExtinguishers();
  }, []);

  const loadNearbyExtinguishers = async (location) => {
    try {
      console.log('Loading extinguishers for location:', location);
      const response = await extinguisherService.getClosest(
        location.lat,
        location.lng,
        10
      );
      
      console.log('API Response:', response);
      
      // Handle different response formats
      let extinguisherData;
      if (response.data && Array.isArray(response.data)) {
        extinguisherData = response.data;
      } else if (Array.isArray(response)) {
        extinguisherData = response;
      } else {
        console.error('Unexpected response format:', response);
        extinguisherData = [];
      }
      
      console.log('Extinguisher data:', extinguisherData);
      
      // Transform data to expected format
      const transformedData = transformExtinguisherData(extinguisherData);
      setNearbyExtinguishers(transformedData);
      setIsLoading(false);
      
    } catch (error) {
      console.error('Failed to load extinguishers:', error);
      setError('Failed to load nearby extinguishers');
      setIsLoading(false);
    }
  };

  if (!apiKey) {
    return (
      <div className="map-container">
        <div className="map-header">
          <h3>🗺️ Nearby Extinguishers</h3>
        </div>
        <div className="map-error">
          <p>Google Maps API Key is missing. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.</p>
        </div>
      </div>
    );
  }

  const createMarkerSvg = (color) => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 24 30"><path fill="${color}" stroke="#FFFFFF" stroke-width="1" d="M12 0C7.03 0 3 4.03 3 9c0 5.25 9 15.25 9 15.25s9-10 9-15.25C21 4.03 16.97 0 12 0zm0 12.5c-1.93 0-3.5-1.57-3.5-3.5S10.07 5.5 12 5.5s3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/></svg>`
    )}`;
  };

  const selectedExtinguisher = nearbyExtinguishers.find(ext => ext.id === selectedExtinguisherId);

  return (
    <APIProvider apiKey={apiKey}>
      <div className="map-container">
        <div className="map-header">
          <h3>🗺️ Nearby Extinguishers</h3>
          <p>Interactive map showing fire extinguisher locations. Click a marker for details.</p>
        </div>
        
        <div className="map-wrapper">
          {isLoading && (
            <div className="map-loading">
              <p>Loading map and nearby extinguishers...</p>
            </div>
          )}
          
          {error && (
            <div className="map-error">
              <p>{error}</p>
            </div>
          )}
          
          <div className="map-display">
            <Map
              defaultCenter={DEFAULT_CENTER}
              defaultZoom={DEFAULT_ZOOM}
              mapId="firewatch_map"
              gestureHandling={'greedy'}
              disableDefaultUI={true}
              className="google-map"
              onClick={() => setSelectedExtinguisherId(null)}
            >
              {/* User location marker */}
              {userLocation && (
                <AdvancedMarker
                  position={userLocation}
                  title="Your Location"
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                  }}
                >
                  <img 
                    src={createMarkerSvg(MARKER_ICON_MAIN_COLOR)} 
                    alt="Your location" 
                    style={{ width: '32px', height: '40px' }} 
                  />
                </AdvancedMarker>
              )}

              {/* Extinguisher markers */}
              {nearbyExtinguishers.map(ext =>
                ext.location && ext.id && (
                  <AdvancedMarker
                    key={ext.id}
                    position={ext.location}
                    title={ext.address}
                    onClick={(e) => {
                      e.domEvent.stopPropagation();
                      setSelectedExtinguisherId(ext.id);
                    }}
                  >
                    <img 
                      src={createMarkerSvg(MARKER_ICON_EXTINGUISHER_COLOR)} 
                      alt={`Fire extinguisher at ${ext.address}`} 
                      style={{ width: '32px', height: '40px' }} 
                    />
                  </AdvancedMarker>
                )
              )}

              {/* Info window for selected extinguisher */}
              {selectedExtinguisher && selectedExtinguisher.location && window.google && window.google.maps && (
                <InfoWindow
                  position={selectedExtinguisher.location}
                  onCloseClick={() => setSelectedExtinguisherId(null)}
                  pixelOffset={new google.maps.Size(0, -35)}
                >
                  <div className="info-window">
                    <h4>{selectedExtinguisher.address}</h4>
                    {selectedExtinguisher.expirationDates.length > 0 && (
                      <p><strong>Expires:</strong> {new Date(selectedExtinguisher.expirationDates[0] + 'T00:00:00').toLocaleDateString()}</p>
                    )}
                    {selectedExtinguisher.distance && (
                      <p><strong>Distance:</strong> {selectedExtinguisher.distance} miles</p>
                    )}
                    {selectedExtinguisher.imageUrl && (
                      <img 
                        src={selectedExtinguisher.imageUrl} 
                        alt="Fire extinguisher" 
                        className="info-window-image"
                      />
                    )}
                  </div>
                </InfoWindow>
              )}

              <MapController
                userLocation={userLocation}
                nearbyExtinguishers={nearbyExtinguishers}
              />
              <ZoomControls />
            </Map>
          </div>
        </div>

        {/* Location info */}
        {userLocation && (
          <div className="location-info">
            <h4>📍 Your Location</h4>
            <p>Showing extinguishers near your current location</p>
          </div>
        )}

        {/* Extinguisher list */}
        <div className="extinguisher-list">
          <h4>🧯 Nearby Fire Extinguishers ({nearbyExtinguishers.length})</h4>
          {isLoading ? (
            <div className="loading">Loading extinguishers...</div>
          ) : nearbyExtinguishers.length > 0 ? (
            <ul>
              {nearbyExtinguishers.map(ext => (
                <li key={ext.id} className="extinguisher-item">
                  <div className="extinguisher-info">
                    <p><strong>{ext.address}</strong></p>
                    <p>Expires: {new Date(ext.expirationDates[0] + 'T00:00:00').toLocaleDateString()}</p>
                    {ext.distance && <p>Distance: {ext.distance} miles</p>}
                    {ext.createdAt && (
                      <p className="created-at">Added: {new Date(ext.createdAt).toLocaleString()}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No fire extinguishers found nearby.</p>
          )}
        </div>
      </div>
    </APIProvider>
  );
}

export default MapDisplay;